import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import GameSignals from '@/signals/GameSignals'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { paintViewportCoverOverlay } from '@dragonframework/modules'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'
import {
    GRADIENT_GOLD_FILL,
    FEATURE_INTRO_IN_ANIMATION_MS,
    fadeOutFeaturePopupItem,
} from './FeatureIntroPopupComponent'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import Facade from '@/Facade'
import { toCurrencySymbol, formatAmount } from '@modules/src/panel/PanelUtils'

const FEATURE_SUMMARY_OVERLAY_OPACITY = 0.7
let featureWinAmountReveal: gsap.core.Tween | null = null
let featureSummaryContinueReveal: gsap.core.Tween | null = null

const translate = (key: string, fallback: string): string => {
    const translated = TranslationUtils.translate(key)
    return translated === key ? fallback : translated
}
let featureSummarySkipUnlockCall: gsap.core.Tween | null = null

// Smaller swing than the 1.1 used on 'Feature Summary Continue': this text is 125px, so the
// same ratio moves a lot more pixels.
const FEATURE_WIN_AMOUNT_PULSE_SCALE = 1.06
const FEATURE_WIN_AMOUNT_PULSE_DURATION_S = 0.6
/** Min time the H&W / FS end popup stays undismissable (rapid click / Space / Spin). */
const FEATURE_SUMMARY_MIN_VISIBLE_S = 0.9

let featureBetterLuckReveal: gsap.core.Tween | null = null

type FeatureSummaryState = {
    canContinue: boolean
    isExiting: boolean
    introComplete: boolean
    minVisibleElapsed: boolean
    tapHandler: (() => void) | null
}

const clearFeatureSummaryInputBlock = (): void => {
    ;(Facade as any).isFeatureSummaryPresentationActive = false
    if (!(Facade as any).isBuyBonusPopupOpen && !Facade.model?.isBuyBonusPending) {
        Facade.isPopupBlockingInput = false
    }
}

const tryEnableFeatureSummaryContinue = (state?: FeatureSummaryState | null): void => {
    if (!state || state.isExiting) {
        return
    }

    if (state.introComplete && state.minVisibleElapsed) {
        state.canContinue = true
    }
}

//## States: 
//## baked: The default, baked-in 'Congratulations' + 'You Won' headline in the box's spine animation.
//## overlay: A custom headline art that is rendered in the overlay, to support locales that don't have a baked-in headline.
const SUMMARY_SPINE_ANIMATIONS = {
    baked: { in: 'you_won_in', loop: 'you_won_loop', out: 'you_won_out' },
    overlay: { in: 'ml_you_won_in', loop: 'ml_you_won_loop', out: 'ml_you_won_out' },
} as const

//## Add new cases here as locales arrive. Anything other than 'baked' must render its own
//## headline art through a matching overlay item below.
type SummaryHeadline = 'baked' | 'betterLuck'

const resolveSummaryHeadline = (data: ICustomEventData): SummaryHeadline => {
    const winAmount = Number(data.signalData ?? 0)

    if (!Number.isFinite(winAmount) || winAmount <= 0) {
        return 'betterLuck'
    }

    return 'baked'
}

const usesOverlayHeadline = (data: ICustomEventData): boolean => resolveSummaryHeadline(data) !== 'baked'

export const FREE_SPINS_SUMMARY_POPUPS_COMPONENT: IComponent = {
    name: ContainersNames.FREE_SPINS_SUMMARY_POPUPS_CONTAINER,
    type: AssetType.CONTAINER,
    zIndex: new Map<Orientation, number>([
        [Orientation.LANDSCAPE, 420],
        [Orientation.PORTRAIT, 420],
    ]),
    visible: false,
    events: [
        {
            event: ComponentEvent.SHOW,
            onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary],
        },
        {
            event: ComponentEvent.HIDE,
            onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete, GameSignals.bonusStop],
        },
    ],
    items: [
        {
            name: 'Free Spins Summary Overlay',
            type: AssetType.OVERLAY,
            componentStyle: {
                color: 0x01102a,
                opacity: FEATURE_SUMMARY_OVERLAY_OPACITY,
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary, ModuleSignals.gameResized],
                    callback: function (data: ICustomEventData) {
                        paintViewportCoverOverlay(
                            data.component.componentElement as PIXI.Graphics,
                            0x01102a,
                            FEATURE_SUMMARY_OVERLAY_OPACITY
                        )
                    },
                },
            ],
        },
        {
            name: 'Feature Summary Spine',
            type: AssetType.SPINE,
            visible: false,
            src: 'popup_message_box',
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: ['you_won_loop'],
                    loops: [true],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary],
                    callback: function (data: ICustomEventData) {
                        const animation: SpineAnimation = data.component.componentElement
                        const summaryAnimations = usesOverlayHeadline(data)
                            ? SUMMARY_SPINE_ANIMATIONS.overlay
                            : SUMMARY_SPINE_ANIMATIONS.baked
                        const parentContainer: any =
                            data.component.parentContainer ||
                            (animation as any)?.parent ||
                            data.component.componentElement

                        // Block Space / Spin / global skip while the end popup is up.
                        Facade.isPopupBlockingInput = true
                        ;(Facade as any).isFeatureSummaryPresentationActive = true

                        if (parentContainer) {
                            const state = (parentContainer.__summaryState ??= {
                                canContinue: false,
                                isExiting: false,
                                introComplete: false,
                                minVisibleElapsed: false,
                                tapHandler: null,
                            }) as FeatureSummaryState

                            state.canContinue = false
                            state.isExiting = false
                            state.introComplete = false
                            state.minVisibleElapsed = false

                            if (state.tapHandler) {
                                parentContainer.off('pointertap', state.tapHandler)
                            }

                            parentContainer.interactive = true
                            parentContainer.eventMode = 'static'
                            parentContainer.cursor = 'pointer'

                            state.tapHandler = () => {
                                if (!state.canContinue || state.isExiting) {
                                    return
                                }

                                state.isExiting = true
                                featureSummarySkipUnlockCall?.kill()
                                featureSummarySkipUnlockCall = null

                                //## Emit the dismiss signal before starting the out animation, so any other components can fade out in sync with it.
                                ;(GameSignals as any).featurePopupDismissStarted?.emit()
                                try {
                                    animation.start([summaryAnimations.out], [false], 0, [1], () => {
                                        GameSignals.showFreeSpinsSummaryComplete.emit()
                                    })
                                } catch (error) {
                                    console.warn('[FreeSpinsSummaryPopup] Failed to play outro animation:', error)
                                    GameSignals.showFreeSpinsSummaryComplete.emit()
                                }
                            }

                            parentContainer.on('pointertap', state.tapHandler)

                            featureSummarySkipUnlockCall?.kill()
                            featureSummarySkipUnlockCall = gsap.delayedCall(FEATURE_SUMMARY_MIN_VISIBLE_S, () => {
                                featureSummarySkipUnlockCall = null
                                state.minVisibleElapsed = true
                                tryEnableFeatureSummaryContinue(state)
                            })
                        }

                        try {
                            animation.start([summaryAnimations.in], [false], 0, [1], () => {
                                const state = parentContainer?.__summaryState as FeatureSummaryState | undefined
                                if (state?.isExiting) {
                                    return
                                }

                                animation.start([summaryAnimations.loop], [true], 0, [1])
                                if (state) {
                                    state.introComplete = true
                                    tryEnableFeatureSummaryContinue(state)
                                }
                            })
                            requestAnimationFrame(() => {
                                animation.visible = true
                            })
                        } catch (error) {
                            console.warn('[FreeSpinsSummaryPopup] Failed to play intro animation:', error)
                        }
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete, GameSignals.bonusStop],
                    callback: function (data: ICustomEventData) {
                        const animation: SpineAnimation = data.component.componentElement
                        const parentContainer: any =
                            data.component.parentContainer ||
                            (animation as any)?.parent ||
                            data.component.componentElement

                        featureSummarySkipUnlockCall?.kill()
                        featureSummarySkipUnlockCall = null
                        clearFeatureSummaryInputBlock()

                        if (!parentContainer) {
                            try {
                                animation.stop()
                            } catch (error) {}
                            animation.visible = false
                            return
                        }

                        const state = parentContainer?.__summaryState as FeatureSummaryState | undefined
                        if (state) {
                            state.isExiting = false
                            state.canContinue = false
                            state.introComplete = false
                            state.minVisibleElapsed = false
                        }
                        if (state?.tapHandler) {
                            parentContainer.off('pointertap', state.tapHandler)
                            state.tapHandler = null
                        }
                        if (parentContainer) {
                            parentContainer.interactive = false
                            parentContainer.eventMode = 'auto'
                            parentContainer.cursor = 'default'
                        }

                        try {
                            animation.stop()
                        } catch (error) {}
                        animation.visible = false
                    },
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.7,
                    y: 0.7,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0.7,
                    y: 0.7,
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 960,
                    y: 357,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 540,
                    y: 906,
                },
            ],
        },
        {
            name: 'Feature Summary Better Luck',
            type: AssetType.SPRITE,
            src: 'better_luck_next_time_text.png',
            anchor: 0.5,
            visible: false,
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.75,
                    y: 0.75,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0.8,
                    y: 0.8,
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 612,
                    y: 80,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 180,
                    y: 635,
                },
            ],
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary],
                    callback: function (data: ICustomEventData) {
                        featureBetterLuckReveal?.kill()

                        const el = data.component.componentElement as PIXI.Sprite
                        gsap.killTweensOf(el)

                        if (resolveSummaryHeadline(data) !== 'betterLuck') {
                            el.visible = false
                            el.alpha = 1
                            return
                        }

                        // Same reveal cadence as the win amount and continue label.
                        featureBetterLuckReveal = gsap.delayedCall(FEATURE_INTRO_IN_ANIMATION_MS / 1000, () => {
                            el.alpha = 1
                            el.visible = true
                        })
                    },
                },
                {
                    event: ComponentEvent.HIDE,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete, GameSignals.bonusStop],
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featurePopupDismissStarted],
                    callback: function (data: ICustomEventData) {
                        featureBetterLuckReveal?.kill()
                        fadeOutFeaturePopupItem(data)
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete, GameSignals.bonusStop],
                    callback: function () {
                        featureBetterLuckReveal?.kill()
                    },
                },
            ],
        },
        {
            name: 'Feature Win Amount',
            type: AssetType.TEXT,
            translationKey: '',
            anchor: 0.5,
            visible: false,
            textStyle: [
                {
                    orientation: Orientation.ANY,
                    options: {
                        fontFamily: 'MyriadPro-Black',
                        fontSize: 125,
                        fontWeight: '900',
                        fill: GRADIENT_GOLD_FILL,
                        stroke: { color: '#7A4E00', width: 2 },
                        dropShadow: {
                            color: '#3A2500',
                            blur: 4,
                            angle: Math.PI / 2,
                            distance: 4,
                            alpha: 1,
                        },
                        align: 'center',
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 961,
                    y: 520,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 545,
                    y: 1070,
                },
            ],
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary],
                    callback: function (data: ICustomEventData) {
                        const winAmountInCoins = Number(data.signalData ?? 0)

                        featureWinAmountReveal?.kill()
                        featureWinAmountReveal = gsap.delayedCall(FEATURE_INTRO_IN_ANIMATION_MS / 1000, () => {
                            const currency = (Facade as any).model?.initData?.player?.currency ?? ''
                            data.component.setText(toCurrencySymbol(currency) + formatAmount(toMajorUnits(winAmountInCoins)))

                            const el = data.component.componentElement as PIXI.Text
                            el.alpha = 1
                            el.visible = true
                            el.anchor.set(0.5)

                            // Pulses for as long as the summary is on screen, for both FS and H&W.
                            gsap.killTweensOf(el.scale)
                            el.scale.set(1)
                            gsap.to(el.scale, {
                                x: FEATURE_WIN_AMOUNT_PULSE_SCALE,
                                y: FEATURE_WIN_AMOUNT_PULSE_SCALE,
                                duration: FEATURE_WIN_AMOUNT_PULSE_DURATION_S,
                                ease: 'sine.inOut',
                                yoyo: true,
                                repeat: -1,
                            })
                        })
                    },
                },
                {
                    event: ComponentEvent.HIDE,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete, GameSignals.bonusStop],
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featurePopupDismissStarted],
                    callback: function (data: ICustomEventData) {
                        featureWinAmountReveal?.kill()
                        fadeOutFeaturePopupItem(data)
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete, GameSignals.bonusStop],
                    callback: function (data: ICustomEventData) {
                        featureWinAmountReveal?.kill()

                        const el = data.component.componentElement as PIXI.Text
                        gsap.killTweensOf(el.scale)
                        el.scale.set(1)
                    },
                },
            ],
        },
        {
            name: 'Feature Summary Continue',
            type: AssetType.TEXT,
            translationKey: '',
            anchor: 0.5,
            visible: false,
            textStyle: [
                {
                    orientation: Orientation.ANY,
                    options: {
                        fontFamily: 'Montserrat_Bold',
                        fontSize: 50,
                        fill: 0xffffff,
                        align: 'center',
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 965,
                    y: 870,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 550,
                    y: 1420,
                },
            ],
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary],
                    callback: function (data: ICustomEventData) {
                        featureSummaryContinueReveal?.kill()
                        // Reveal with the skip unlock so "Tap to Continue" is not shown while taps are ignored.
                        const continueRevealDelayS = Math.max(
                            FEATURE_INTRO_IN_ANIMATION_MS / 1000,
                            FEATURE_SUMMARY_MIN_VISIBLE_S
                        )
                        featureSummaryContinueReveal = gsap.delayedCall(continueRevealDelayS, () => {
                            const key = DeviceUtils.isMobileDevice() ? 'Tap to Continue' : 'Click to Continue'
                            data.component.setText(translate(key, key))

                            const el = data.component.componentElement as PIXI.Text
                            el.alpha = 1
                            el.visible = true
                            el.anchor.set(0.5)
                            gsap.killTweensOf(el.scale)
                            el.scale.set(1)
                            gsap.to(el.scale, {
                                x: 1.1,
                                y: 1.1,
                                duration: 0.6,
                                ease: 'sine.inOut',
                                yoyo: true,
                                repeat: -1,
                            })
                        })
                    },
                },
                {
                    event: ComponentEvent.HIDE,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete, GameSignals.bonusStop],
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featurePopupDismissStarted],
                    callback: function (data: ICustomEventData) {
                        featureSummaryContinueReveal?.kill()
                        fadeOutFeaturePopupItem(data)
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete, GameSignals.bonusStop],
                    callback: function (data: ICustomEventData) {
                        featureSummaryContinueReveal?.kill()
                        featureSummarySkipUnlockCall?.kill()
                        featureSummarySkipUnlockCall = null
                        clearFeatureSummaryInputBlock()

                        const el = data.component.componentElement as PIXI.Text
                        gsap.killTweensOf(el.scale)
                        el.scale.set(1)
                    },
                },
            ],
        },
    ],
}
